/**
 * Unit Zone Schedule
 * - display a list of thermostat schedules for all unit zones
 * - sort by schedule types like 'Custom', 'SameEveryDay' or 'Weekday/Weekend'
 */
import React, { useState, useEffect } from 'react';
/* MUI */
import { Box, Card, CardHeader, IconButton, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
/* HELPERS */
import {
  WdTab,
  WdTabs,
} from '../../../devices/residential-devices/thermostat/components/schedule/weekday-tabs';
/* TYPES */
import {
  DayOfWeek,
  EmptyWeeklySchedule,
} from '../../../devices/residential-devices/thermostat/types/schedule-types';
import {
  EnumScheduleType,
  Zone,
  useUpdateThermostatPendingScheduleMutation,
  useThermostatDetailQuery,
  ThermostatScheduleTemplateValue,
  ThermostatPendingSchedule,
  TemperatureUnit,
} from '../../../../../types/generated-types';
import { ThermostatScheduleTable } from '../../../devices/residential-devices/thermostat/components/schedule/thermostat-schedule-table';
import { daysToDisplay } from '../../../devices/residential-devices/thermostat/components/helpers/days-to-display';
import { ThermostatClearPendingScheduleDialog } from './thermostat-pending-schedule-clear-dialog';
import { Notifier } from '../../../../system/services/notificationManager';
import { MyThermostat } from '../../../devices/residential-devices/thermostat/types/my-thermostat';
import { ThermostatEditPendingScheduleDialog } from './thermostat-edit-pending-schedule-dialog';
import _ from 'lodash';
import { Fahrenheit } from '../../../../system/models/temperatureUnits';
import { useAuthenticator } from '../../../../auth/AuthenticationContext';
import { convertToPreferredWeeklyScheduleUnits } from '../../../devices/util/convert-to-preferred-schedule-units';
import { defaultAbsoluteLimits } from '../../../helpers';
import {
  allDaysEqual,
  weekdaysEqual,
  weekendsEqual,
} from '../helpers/checkScheduleType';
import { useIsSmallScreen } from '../../../../system/SystemMediaQueriesProvider';

const dayOfWeekLabels: Record<DayOfWeek, string> = {
  [DayOfWeek.SU]: 'SU',
  [DayOfWeek.MO]: 'MO',
  [DayOfWeek.TU]: 'TU',
  [DayOfWeek.WE]: 'WE',
  [DayOfWeek.TH]: 'TH',
  [DayOfWeek.FR]: 'FR',
  [DayOfWeek.SA]: 'SA',
};
const label: string[] = ['Weekday', 'Weekend'];

interface UnitZoneScheduleProps {
  zone?: Zone;
  index?: number;
}

export function UnitZoneSchedule(props: UnitZoneScheduleProps) {
  const { zone, index } = props;

  const [zoneThermostat, setZoneThermostat] = useState<MyThermostat>();
  const [originalScheduleType, setOriginalScheduleType] =
    useState<EnumScheduleType>(EnumScheduleType.None);
  const [selectedScheduleType, setSelectedScheduleType] =
    useState<EnumScheduleType>(EnumScheduleType.None);
  const [open, setOpen] = useState(false);
  const [openEditPendingScheduleDialog, setOpenEditPendingScheduleDialog] =
    useState(false);
  const [scheduleValues, setScheduleValues] = React.useState<
    ThermostatScheduleTemplateValue | ThermostatPendingSchedule
  >({
    id: '',
    units: 'N/A',
    timestamp: 0,
    value: EmptyWeeklySchedule,
    updatePending: false,
  });

  // TODO: Peter i (Calvin) commented out this subscription due to
  // The Int "0" exception also affecting the subcription while
  // Updating the pendingSchedule field
  // useThermostatPendingScheduleUpdateSubscription({
  //   variables: { id: zone?.thermostat?._id ?? '' },
  //   fetchPolicy: 'no-cache',
  //   onData: updateCacheFromSubscriptionEvent,
  // });

  const {
    data,
    loading,
    // TODO: Loading_error: should we handle error here for this query?
    // error,
  } = useThermostatDetailQuery({
    variables: { id: zone?.thermostat?._id ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const auth = useAuthenticator();

  const preferredUnits = auth.user?.preferredUnits ?? Fahrenheit;

  useEffect(() => {
    if (zone?.thermostat?.pendingSchedule) {
      const formattedTemplateValues = convertToPreferredWeeklyScheduleUnits(
        _.cloneDeep(zone?.thermostat?.pendingSchedule.value),
        preferredUnits,
        TemperatureUnit.C,
      );
      let schedType = EnumScheduleType.None;
      if (allDaysEqual(zone?.thermostat?.pendingSchedule.value)) {
        schedType = EnumScheduleType.SameEveryDay;
      } else if (
        weekdaysEqual(zone?.thermostat?.pendingSchedule.value) &&
        weekendsEqual(zone?.thermostat?.pendingSchedule.value)
      ) {
        schedType = EnumScheduleType.WeekdayWeekend;
      } else {
        schedType = EnumScheduleType.Custom;
      }
      setScheduleValues({
        ..._.cloneDeep(zone?.thermostat?.pendingSchedule),
        value: formattedTemplateValues,
      });
      setOriginalScheduleType(schedType);
      setSelectedScheduleType(schedType);
    }
  }, [zone, preferredUnits, openEditPendingScheduleDialog]);

  useEffect(() => {
    const thermostat = data && (data.thermostatById as MyThermostat);
    if (thermostat) {
      setZoneThermostat(thermostat);
    }
  }, [data]);

  const [selectedDay, setSelectedDay] = useState(
    selectedScheduleType === EnumScheduleType.WeekdayWeekend
      ? DayOfWeek.MO
      : DayOfWeek.SU,
  );
  const isSmall = useIsSmallScreen();

  const dailySchedule = zoneThermostat?.schedule?.value[selectedDay] ?? {};
  const pendingDailySchedule =
    zoneThermostat?.pendingSchedule?.value[selectedDay] ?? {};

  // Get tab labels by schedule type
  // Example: If schedule type is 'SameEveryDay' then only display one tab label 'Every day'
  function getTabLabel(dayOfWeek: DayOfWeek): string {
    if (selectedScheduleType === EnumScheduleType.SameEveryDay) {
      return 'Everyday';
    } else {
      return dayOfWeekLabels[dayOfWeek];
    }
  }

  // View change handler
  function handleViewChange(event: React.SyntheticEvent, newValue: DayOfWeek) {
    setSelectedDay(newValue);
  }

  const [updateThermostatPendingSchedule] =
    useUpdateThermostatPendingScheduleMutation({
      refetchQueries: ['ThermostatDetail'],
    });

  const handleClearPendingSchedule = () => {
    if (zoneThermostat?.deviceId) {
      updateThermostatPendingSchedule({
        variables: {
          deviceId: zoneThermostat?.deviceId,
          update: {
            pendingSchedule: {
              '0': {},
              '1': {},
              '2': {},
              '3': {},
              '4': {},
              '5': {},
              '6': {},
            },
          },
        },
      })
        .then(() => {
          Notifier.success('Pending Schedule Cleared');
        })
        .catch((error) => {
          console.log('ERROR clearing pending schedule: ', error);
          Notifier.error('Error Clearing Pending Schedule');
        });
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenEditPendingScheduleDialog = () => {
    setOpenEditPendingScheduleDialog(true);
  };

  const handleCloseEditPendingScheduleDialog = () => {
    setOpenEditPendingScheduleDialog(false);
  };

  const handleUpdatePendingSchedule = () => {
    if (zoneThermostat?.deviceId) {
      const formattedScheduleValues = convertToPreferredWeeklyScheduleUnits(
        scheduleValues.value,
        'C',
        preferredUnits,
      );
      updateThermostatPendingSchedule({
        variables: {
          deviceId: zoneThermostat?.deviceId,
          update: {
            pendingSchedule: formattedScheduleValues,
          },
        },
      })
        .then(() => {
          Notifier.success('Updated Pending Schedule');
          handleCloseEditPendingScheduleDialog();
        })
        .catch((error) => {
          console.log('ERROR updating pending schedule: ', error);
          Notifier.error('Error Updating Pending Schedule, Please Try Again');
        });
    }
  };

  const pendingScheduleHasChanges =
    !_.isEqual(
      zone?.thermostat?.pendingSchedule?.value,
      convertToPreferredWeeklyScheduleUnits(
        scheduleValues.value,
        'C',
        preferredUnits,
      ),
    ) || originalScheduleType !== selectedScheduleType;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Card
        key={`${zone?._id} -${index}`}
        sx={{
          background: 'inherit',
          margin: isSmall ? '0px' : '0px 20px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          alignItems: 'stretch',
        }}
        elevation={4}
      >
        <CardHeader
          title={
            <Typography variant="h4" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
              {zone?.name ?? 'N/A'}
            </Typography>
          }
        />
        {Object.keys(dailySchedule).length > 0 ? (
          <>
            <WdTabs
              variant="fullWidth"
              value={selectedDay}
              onChange={handleViewChange}
            >
              {daysToDisplay(selectedScheduleType).map((dayOfWeek, index) => {
                return (
                  <WdTab
                    key={dayOfWeek}
                    value={dayOfWeek}
                    label={
                      daysToDisplay(selectedScheduleType).length === 2 &&
                      selectedScheduleType === EnumScheduleType.WeekdayWeekend
                        ? label[index]
                        : getTabLabel(dayOfWeek)
                    }
                  />
                );
              })}
            </WdTabs>
            <ThermostatScheduleTable dailySchedule={dailySchedule} />
          </>
        ) : (
          <div style={{ padding: '20px' }}>
            <Typography sx={{ textAlign: 'center' }}>
              No Applied Schedule
            </Typography>
          </div>
        )}

        <br />
      </Card>
      <br />
      {Object.keys(pendingDailySchedule).length > 0 ? (
        <Card
          key={`${zone?._id} -${index}-pending-schedule`}
          sx={{
            background: 'inherit',
            margin: isSmall ? '0px' : '0px 20px',
          }}
          elevation={4}
        >
          <CardHeader
            title={
              <Typography variant="h4" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                {`${zone?.name + ' (Pending Schedule)' ?? 'N/A'}`}
              </Typography>
            }
            action={
              <div>
                <IconButton
                  aria-label="edit pending schedule settings"
                  onClick={() => handleOpenEditPendingScheduleDialog()}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  aria-label="clear pending schedule settings"
                  onClick={() => handleOpen()}
                >
                  <ClearIcon />
                </IconButton>
              </div>
            }
          />
          <>
            <WdTabs
              variant="fullWidth"
              value={selectedDay}
              onChange={handleViewChange}
            >
              {daysToDisplay(selectedScheduleType).map((dayOfWeek, index) => {
                return (
                  <WdTab
                    key={dayOfWeek}
                    value={dayOfWeek}
                    label={
                      daysToDisplay(selectedScheduleType).length === 2 &&
                      selectedScheduleType === EnumScheduleType.WeekdayWeekend
                        ? label[index]
                        : getTabLabel(dayOfWeek)
                    }
                  />
                );
              })}
            </WdTabs>
            <ThermostatScheduleTable dailySchedule={pendingDailySchedule} />
          </>
          <br />
        </Card>
      ) : null}
      <ThermostatClearPendingScheduleDialog
        open={open}
        handleClearPendingSchedule={() => handleClearPendingSchedule()}
        handleClose={() => handleClose()}
      />
      <ThermostatEditPendingScheduleDialog
        open={openEditPendingScheduleDialog}
        setScheduleValues={setScheduleValues}
        setpointLimitSettings={{
          maxHeatSetpointLimit:
            zone?.thermostat?.maxHeatSetpointLimit?.value ??
            defaultAbsoluteLimits[preferredUnits].heat.max,
          maxCoolSetpointLimit:
            zone?.thermostat?.maxCoolSetpointLimit?.value ??
            defaultAbsoluteLimits[preferredUnits].cool.max,
          minHeatSetpointLimit:
            zone?.thermostat?.minHeatSetpointLimit?.value ??
            defaultAbsoluteLimits[preferredUnits].heat.min,
          minCoolSetpointLimit:
            zone?.thermostat?.minCoolSetpointLimit?.value ??
            defaultAbsoluteLimits[preferredUnits].cool.min,
        }}
        handleUpdatePendingSchedule={handleUpdatePendingSchedule}
        scheduleValues={scheduleValues ?? null}
        scheduleType={selectedScheduleType}
        setScheduleType={setSelectedScheduleType}
        pendingScheduleHasChanges={pendingScheduleHasChanges}
        handleClose={() => handleCloseEditPendingScheduleDialog()}
      />
    </Box>
  );
}
